import { GameAdministration } from './GameAdministration.js';
import { useEffect, useRef, useState } from 'react';
import type { FormationQueue, SessionContext, TeamRequestStatus } from '@nerds-at-play/contracts';
import { entryApi, entryMessage, EntryError } from './formation-api.js';
/** The sign-in callback returns here with a generic failure flag; consume it once. */
function gmLoginFailure() {
  const url = new URL(window.location.href);
  if (url.searchParams.get('gm_login') !== 'failed') return '';
  url.searchParams.delete('gm_login');
  window.history.replaceState(window.history.state, '', url);
  return 'Game Master sign-in did not complete. Use an account in the Game Master group, then try again.';
}
export function GmRequestPanel({
  session,
  onSession,
}: {
  session: SessionContext | null;
  onSession: (session: SessionContext | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(gmLoginFailure);
  const [syncError, setSyncError] = useState('');
  const [notice, setNotice] = useState('');
  const [queue, setQueue] = useState<FormationQueue | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [reset, setReset] = useState<TeamRequestStatus | null>(null);
  const [newPin, setNewPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const retry = useRef<{ body: string; key: string } | null>(null);
  const generation = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  async function refresh() {
    const current = ++generation.current;
    try {
      const result = await entryApi<FormationQueue>(
        `/api/formation/queue${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      );
      if (current === generation.current) {
        setQueue(result);
        setSyncError('');
      }
    } catch (e) {
      if (current !== generation.current) return;
      setSyncError(entryMessage(e));
      if (e instanceof EntryError && (e.status === 401 || e.status === 403)) {
        setQueue(null);
        setReset(null);
        setNewPin('');
        setConfirmation('');
        onSession(null);
      }
    }
  }
  useEffect(() => {
    if (session?.role !== 'MODERATOR') return;
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 5000);
    return () => {
      clearInterval(timer);
      generation.current++;
    };
    // Session identity and cursor govern the queue lifetime.
  }, [session, cursor]);
  async function command(
    request: TeamRequestStatus,
    operation: 'decision' | 'pin' | 'revoke',
    action?: 'APPROVE' | 'REJECT',
  ) {
    if (operation === 'pin' && newPin !== confirmation) {
      setError('The two PINs must match.');
      return;
    }
    generation.current++;
    setBusy(true);
    setError('');
    setNotice('');
    const payload =
      operation === 'pin'
        ? { pin: newPin, confirmPin: confirmation, expectedVersion: request.version }
        : operation === 'revoke'
          ? { expectedVersion: request.version }
          : { action, expectedVersion: request.version };
    const body = JSON.stringify({ requestId: request.requestId, operation, ...payload });
    if (retry.current?.body !== body) retry.current = { body, key: crypto.randomUUID() };
    try {
      await entryApi(`/api/formation/requests/${request.requestId}/${operation}`, 'POST', {
        ...payload,
        idempotencyKey: retry.current.key,
      });
      if (!alive.current) return;
      setReset(null);
      setNewPin('');
      setConfirmation('');
      retry.current = null;
      await refresh();
      setNotice(
        operation === 'pin'
          ? `New PIN saved for ${request.name}. Previous access is invalidated.`
          : operation === 'revoke'
            ? `Access revoked for ${request.name}. Set a new PIN to restore access.`
            : `${request.name}: ${action === 'APPROVE' ? 'approved' : 'rejected; name released'}.`,
      );
    } catch (e) {
      if (!alive.current) return;
      if (e instanceof EntryError && (e.status === 401 || e.status === 403)) {
        setQueue(null);
        setReset(null);
        setNewPin('');
        setConfirmation('');
        retry.current = null;
        onSession(null);
      }
      if (e instanceof EntryError && e.status === 409) {
        setReset(null);
        setNewPin('');
        setConfirmation('');
        retry.current = null;
        await refresh();
      }
      setError(entryMessage(e));
    } finally {
      setBusy(false);
    }
  }
  if (session?.role === 'TEAM')
    return (
      <p>
        You are signed in as a team. Use Board access to log out before signing in as Game Master.
      </p>
    );
  return (
    <section aria-label="GM team requests">
      {session?.role === 'MODERATOR' && <GameAdministration gameId={session.gameId} />}
      {syncError && (
        <p role="alert" className="entry-error">
          Queue may be out of date. {syncError}
        </p>
      )}
      {error && (
        <p role="alert" className="entry-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="entry-status">
          {notice}
        </p>
      )}
      {session?.role !== 'MODERATOR' ? (
        <>
          <p>
            Sign in with your Game Master account to review team requests and set replacement PINs.
            Approval controls stay on this board.
          </p>
          <button
            type="button"
            className="board-primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              window.location.assign('/api/auth/gm/login');
            }}
          >
            {busy ? 'Opening sign-in…' : 'Sign in as Game Master'}
          </button>
          <p className="entry-status">
            Game Master accounts are created by the operator. Ask the operator in person if you
            cannot sign in.
          </p>
        </>
      ) : (
        <>
          <p>
            Approve teams you recognize. Rejecting a request immediately frees its name; discuss
            questions in person.
          </p>
          <button disabled={busy} onClick={() => void refresh()}>
            Refresh requests
          </button>
          {!queue && <p role="status">Loading team requests…</p>}
          {queue && (
            <>
              <p className="entry-status">
                {queue.formationOpen
                  ? 'Team formation is open'
                  : 'New teams are closed — direct late arrivals to existing teams'}
              </p>
              {queue.requests.length === 0 && <p>No team requests yet.</p>}
              <div className="entry-request-list">
                {queue.requests.map((request) => (
                  <article
                    className="entry-request-card"
                    key={request.requestId}
                    aria-label={request.name}
                  >
                    <h3>{request.name}</h3>
                    <p>
                      <strong>
                        {request.status === 'PENDING'
                          ? 'Pending'
                          : request.status === 'APPROVED'
                            ? request.accessRevoked
                              ? 'Approved · access revoked'
                              : 'Approved'
                            : 'Rejected · name released'}
                      </strong>{' '}
                      ·{' '}
                      <time dateTime={request.createdAt}>
                        {new Date(request.createdAt).toLocaleString()}
                      </time>
                    </p>
                    <div className="entry-actions">
                      {request.status === 'PENDING' && (
                        <>
                          <button
                            className="board-primary"
                            disabled={busy || !queue.formationOpen}
                            onClick={() => void command(request, 'decision', 'APPROVE')}
                          >
                            Approve
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => void command(request, 'decision', 'REJECT')}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {request.status === 'APPROVED' && !request.accessRevoked && (
                        <button disabled={busy} onClick={() => void command(request, 'revoke')}>
                          Revoke team access
                        </button>
                      )}
                      {request.status !== 'REJECTED' && (
                        <button
                          disabled={busy}
                          onClick={() => {
                            setReset(request);
                            setNewPin('');
                            setConfirmation('');
                            setError('');
                          }}
                        >
                          Set new PIN
                        </button>
                      )}
                    </div>
                    {reset?.requestId === request.requestId && (
                      <form
                        onSubmit={(event) => {
                          event.preventDefault();
                          void command(reset, 'pin');
                        }}
                      >
                        <p>
                          Confirm the team in person, then choose a new six-digit PIN. Existing
                          access will stop working.
                        </p>
                        <label>
                          New shared PIN
                          <input
                            type="password"
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            maxLength={6}
                            required
                            autoComplete="new-password"
                            value={newPin}
                            disabled={busy}
                            onChange={(event) => setNewPin(event.target.value)}
                          />
                        </label>
                        <label>
                          Confirm new PIN
                          <input
                            type="password"
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            maxLength={6}
                            required
                            autoComplete="new-password"
                            value={confirmation}
                            disabled={busy}
                            onChange={(event) => setConfirmation(event.target.value)}
                          />
                        </label>
                        <div className="entry-actions">
                          <button className="board-primary" disabled={busy}>
                            Save new PIN
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setReset(null);
                              setNewPin('');
                              setConfirmation('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </article>
                ))}
              </div>
              <div className="entry-actions">
                {cursor && (
                  <button
                    disabled={busy}
                    onClick={() => {
                      setCursor(undefined);
                      setQueue(null);
                    }}
                  >
                    First page
                  </button>
                )}
                {queue.cursor && (
                  <button
                    disabled={busy}
                    onClick={() => {
                      setCursor(queue.cursor);
                      setQueue(null);
                    }}
                  >
                    Next requests
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
